import { getProductInfo } from '@/shbdeployer/product';
import { formatDateTimeSeconds, formatDuration } from '@/shbdeployer/format';
import { buildDiagnosticsReport, collectBrowserInfo, copyToClipboard } from '../../services/diagnostics';

const STATUS_LABELS = {
  operational: 'Работает',
  degraded: 'Деградация',
  down: 'Недоступен',
  unknown: 'Неизвестно',
};

const STATUS_BADGES = {
  operational: 'shb-badge--success',
  degraded: 'shb-badge--warning',
  down: 'shb-badge--error',
  unknown: 'shb-badge--muted',
};

const STATUS_ICONS = {
  operational: 'fa-check-circle',
  degraded: 'fa-exclamation-triangle',
  down: 'fa-times-circle',
  unknown: 'fa-question-circle',
};

const REFRESH_INTERVAL_MS = 30000;

export class SystemStatusController {
  /* @ngInject */
  constructor($async, $interval, ShbSystemService) {
    this.$async = $async;
    this.$interval = $interval;
    this.ShbSystemService = ShbSystemService;
    this.state = { loading: false, error: '' };
    this.status = { overall: 'unknown', components: [] };
    this.readiness = { ready: false };
    this.health = false;
    this.load = this.load.bind(this);
  }

  label(status) {
    return STATUS_LABELS[status] || STATUS_LABELS.unknown;
  }

  badgeClass(status) {
    return STATUS_BADGES[status] || STATUS_BADGES.unknown;
  }

  icon(status) {
    return STATUS_ICONS[status] || STATUS_ICONS.unknown;
  }

  load() {
    return this.$async(async () => {
      this.state.loading = true;
      this.state.error = '';
      try {
        const [status, health, readiness] = await Promise.all([
          this.ShbSystemService.status(),
          this.ShbSystemService.health().catch(() => null),
          this.ShbSystemService.readiness(),
        ]);
        this.status = status;
        this.health = !!health;
        this.readiness = readiness;
        this.checkedAt = formatDateTimeSeconds(status.checkedAt);
        this.uptime = formatDuration(status.uptimeSeconds);
      } catch (err) {
        this.state.error = 'Не удалось получить состояние системы. Сервер приложения не отвечает.';
        this.status = { overall: 'down', components: [] };
        this.health = false;
        this.readiness = { ready: false };
      } finally {
        this.state.loading = false;
      }
    });
  }

  $onInit() {
    this.load();
    this.timer = this.$interval(this.load, REFRESH_INTERVAL_MS);
  }

  $onDestroy() {
    if (this.timer) {
      this.$interval.cancel(this.timer);
    }
  }
}

export class SystemDiagnosticsController {
  /* @ngInject */
  constructor($async, Notifications, ShbSystemService) {
    this.$async = $async;
    this.Notifications = Notifications;
    this.ShbSystemService = ShbSystemService;
    this.state = { loading: false, error: '', copied: false, copiedValue: '' };
    this.load = this.load.bind(this);
  }

  load() {
    return this.$async(async () => {
      this.state.loading = true;
      this.state.error = '';
      try {
        this.data = await this.ShbSystemService.diagnostics();
        this.uptime = formatDuration(this.data.uptimeSeconds);
      } catch (err) {
        this.state.error = 'Не удалось получить диагностические сведения.';
      } finally {
        this.state.loading = false;
      }
    });
  }

  /**
   * Копирует одно значение (идентификатор установки, коммит) в буфер обмена.
   * `field` — имя поля: по нему разметка показывает отметку у нужной строки.
   */
  copyValue(value, field) {
    return this.$async(async () => {
      try {
        await copyToClipboard(String(value === undefined || value === null ? '' : value));
        this.state.copiedValue = field;
      } catch (err) {
        this.Notifications.error('Ошибка', err, 'Не удалось скопировать значение');
      }
    });
  }

  copy() {
    return this.$async(async () => {
      try {
        const status = await this.ShbSystemService.status().catch(() => null);
        const report = buildDiagnosticsReport({ product: getProductInfo(), status, diagnostics: this.data, browser: collectBrowserInfo() });
        await copyToClipboard(report);
        this.state.copied = true;
      } catch (err) {
        this.Notifications.error('Ошибка', err, 'Не удалось скопировать диагностические сведения');
      }
    });
  }

  $onInit() {
    this.load();
  }
}
